import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Bottone, Modale } from '../../componenti/base.tsx'
import { IconaDocumento, IconaScarica } from '../../componenti/Icone.tsx'
import * as posta from '../../servizi/posta.ts'
import { MostraOriginale } from './MostraOriginale.tsx'
import '../../stile-posta-caselle.css'

/**
 * «Stampa», «Salva il messaggio in PDF» e «Scarica .eml» su un messaggio aperto (richiesta di Davide del
 * 2/11/2026), nella webmail e nell'archivio delle caselle. Il secondo si chiamava «Scarica PDF»: chi cercava il
 * PDF arrivato come allegato lo premeva e otteneva il messaggio (4/11/2026, Osservazioni 6 di Carmem).
 *
 * Stampa. Una pagina pulita: oggetto, Da, A, Cc, data, allegati e corpo, senza menu ne' comandi. Il foglio
 * si costruisce nella pagina stessa, in un contenitore che a schermo sta fuori vista e in stampa e' l'unica
 * cosa visibile (stile-posta-caselle.css, `html.stampa-posta-attiva`). Il corpo HTML non va nell'iframe
 * isolato della lettura: un iframe in stampa e' un blocco unico, e i browser lo tagliano alla prima pagina.
 * Va in una shadow root, che tiene separati gli stili del messaggio da quelli del gestionale, con l'HTML
 * gia' ripulito da ripulisciHtml (niente script, attributi on*, style, meta, form, iframe) e sotto la
 * politica dei contenuti della pagina, che non esegue script in linea e non carica immagini remote: le
 * immagini remote restano bloccate come nella lettura, sempre.
 *
 * PDF. Il generatore PDF/A del gestionale (apps/lavori/src/stampa/pdfa.ts) impagina fogli strutturati di
 * testo nel container dei lavori, con un Job asincrono: un'email HTML perderebbe tabelle, grassetti e
 * immagini incorporate, e l'API non ha la libreria. Il PDF si salva quindi dalla finestra di stampa del
 * browser («Salva come PDF»), che rende il messaggio com'e': il comando lo dice prima di aprirla.
 *
 * .eml. Lo compone l'API (servizi/posta-eml.ts): qui si scarica il file.
 */

export type MessaggioDaStampare = {
  oggetto: string
  da: string
  a: string[]
  cc: string[]
  data: string | Date
  /** Casella di gruppo o di progetto: chi l'ha mandato. */
  inviatoDa?: { nome: string; cognome: string } | null
  allegati: { nome: string; dimensione: number }[]
  testo: string | null
  html: string | null
}

const STILE_CORPO = ':host{display:block}'
  + '.corpo{font:11pt/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;color:#14181d;'
  + 'word-break:break-word}'
  + '.corpo *{position:static!important;max-width:100%}'
  + '.corpo img{height:auto}.corpo table{max-width:100%}'
  + '.corpo blockquote{margin:0 0 0 8px;padding-left:10px;border-left:2px solid #d5dae2;color:#4d555f}'
  + '.corpo pre{white-space:pre-wrap}'

/** Il foglio stampato e la finestra di stampa; `pdf` aggiunge prima la spiegazione del «Salva come PDF». */
export function StampaMessaggio({ messaggio, pdf, chiudi }: {
  messaggio: MessaggioDaStampare; pdf: boolean; chiudi: () => void
}) {
  const [fase, setFase] = useState<'spiegazione' | 'stampa'>(pdf ? 'spiegazione' : 'stampa')
  if (fase === 'spiegazione') {
    const allegati = messaggio.allegati
    return (
      <Modale titolo="Salva il messaggio in PDF" chiudi={chiudi}
        azioni={<>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" onClick={() => setFase('stampa')}>Apri la finestra di stampa</Bottone>
        </>}>
        <p>
          Il PDF del messaggio si salva dalla finestra di stampa del browser: come destinazione (o stampante)
          scegli <strong>«Salva come PDF»</strong>, poi «Salva». Il foglio ha oggetto, mittente, destinatari, data,
          l’elenco degli allegati e il testo, senza menu né comandi.
        </p>
        {allegati.length > 0 && (
          <p>
            <strong>Gli allegati non entrano in questo PDF.</strong>{' '}
            {allegati.length === 1
              ? <>«{allegati[0].nome}» si scarica con «Scarica» accanto al suo nome, sotto mittente e destinatari.</>
              : <>I {allegati.length} allegati si scaricano con «Scarica» accanto a ciascun nome, sotto mittente e destinatari.</>}
          </p>
        )}
        <p className="piccolo tenue">Le immagini esterne restano bloccate.</p>
      </Modale>
    )
  }
  return <FoglioDiStampa messaggio={messaggio} fatto={chiudi} />
}

function FoglioDiStampa({ messaggio, fatto }: { messaggio: MessaggioDaStampare; fatto: () => void }) {
  const [contenitore] = useState(() => {
    const div = document.createElement('div')
    div.className = 'stampa-posta'
    div.setAttribute('aria-hidden', 'true')
    return div
  })
  const corpo = useRef<HTMLDivElement>(null)
  const finito = useRef(fatto)
  finito.current = fatto

  useEffect(() => {
    document.body.appendChild(contenitore)
    document.documentElement.classList.add('stampa-posta-attiva')
    let chiuso = false
    const fine = () => {
      if (chiuso) return
      chiuso = true
      document.documentElement.classList.remove('stampa-posta-attiva')
      contenitore.remove()
      finito.current()
    }
    window.addEventListener('afterprint', fine)
    // Un attimo perche' foglio e shadow root siano disegnati prima della finestra di stampa. Un timer e non
    // requestAnimationFrame: in una scheda in secondo piano i fotogrammi si fermano.
    // afterprint arriva alla chiusura della finestra in tutti i browser; print() non sempre aspetta.
    const attesa = window.setTimeout(() => window.print(), 100)
    return () => {
      window.clearTimeout(attesa)
      window.removeEventListener('afterprint', fine)
      document.documentElement.classList.remove('stampa-posta-attiva')
      contenitore.remove()
    }
  }, [contenitore])

  useEffect(() => {
    const host = corpo.current
    if (!host || !messaggio.html) return
    const radice = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
    radice.innerHTML = `<style>${STILE_CORPO}</style><div class="corpo">${posta.ripulisciHtml(messaggio.html, false).html}</div>`
  }, [messaggio.html])

  return createPortal(
    <article className="stampa-posta-foglio">
      <h1>{messaggio.oggetto || '(senza oggetto)'}</h1>
      <dl>
        <dt>Da</dt>
        <dd>
          {posta.nomeEIndirizzo(messaggio.da)}
          {messaggio.inviatoDa && <> · inviato da {messaggio.inviatoDa.nome} {messaggio.inviatoDa.cognome}</>}
        </dd>
        <dt>A</dt>
        <dd>{messaggio.a.map(posta.nomeEIndirizzo).join(', ') || '—'}</dd>
        {messaggio.cc.length > 0 && (
          <>
            <dt>Cc</dt>
            <dd>{messaggio.cc.map(posta.nomeEIndirizzo).join(', ')}</dd>
          </>
        )}
        <dt>Data</dt>
        <dd>{posta.dataEstesa(typeof messaggio.data === 'string' ? messaggio.data : messaggio.data.toISOString())}</dd>
        {messaggio.allegati.length > 0 && (
          <>
            <dt>Allegati</dt>
            <dd>
              {messaggio.allegati.map((a) => `${a.nome} (${Math.max(1, Math.round(a.dimensione / 1024))} KB)`).join(', ')}
            </dd>
          </>
        )}
      </dl>
      {messaggio.html
        ? <div ref={corpo} />
        : <p className="stampa-posta-testo">{messaggio.testo || '(messaggio senza testo)'}</p>}
    </article>,
    contenitore,
  )
}

/** I comandi sotto le intestazioni del messaggio aperto. */
export function ComandiMessaggio({ messaggio, percorsoEml, percorsoOriginale, prima }: {
  messaggio: MessaggioDaStampare
  /** La rotta del .eml: della webmail o dell'archivio delle caselle. */
  percorsoEml: string
  /** «Mostra originale» (539), intestazioni e sorgente: la rotta della webmail o dell'archivio delle caselle. */
  percorsoOriginale?: string
  /** Comandi del messaggio prima di questi: nella webmail «Inoltra» e «Inoltra come allegato» (3/11/2026). */
  prima?: ReactNode
}) {
  const [stampa, setStampa] = useState<'stampa' | 'pdf' | null>(null)
  const [mostra, setMostra] = useState(false)
  const [stato, setStato] = useState<{ inCorso: boolean; errore: string | null }>({ inCorso: false, errore: null })

  async function eml() {
    setStato({ inCorso: true, errore: null })
    try {
      await posta.scaricaFile(percorsoEml)
      setStato({ inCorso: false, errore: null })
    } catch (e) {
      setStato({ inCorso: false, errore: e instanceof Error ? e.message : 'Scaricamento non riuscito.' })
    }
  }

  return (
    <div className="posta-messaggio-comandi">
      {prima}
      <Bottone variante="discreto" dimensione="piccolo" icona={<IconaDocumento />} onClick={() => setStampa('stampa')}>
        Stampa
      </Bottone>
      <Bottone variante="discreto" dimensione="piccolo" icona={<IconaDocumento />} onClick={() => setStampa('pdf')}>
        Salva il messaggio in PDF
      </Bottone>
      <Bottone variante="discreto" dimensione="piccolo" icona={<IconaScarica />} inCorso={stato.inCorso}
               onClick={() => void eml()}>
        Scarica .eml
      </Bottone>
      {percorsoOriginale && (
        <Bottone variante="discreto" dimensione="piccolo" icona={<IconaDocumento />} onClick={() => setMostra(true)}>
          Mostra originale
        </Bottone>
      )}
      {stato.errore && <span className="piccolo" role="alert">{stato.errore}</span>}
      {stampa && <StampaMessaggio messaggio={messaggio} pdf={stampa === 'pdf'} chiudi={() => setStampa(null)} />}
      {mostra && percorsoOriginale && <MostraOriginale percorso={percorsoOriginale} chiudi={() => setMostra(false)} />}
    </div>
  )
}
